import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ArtifactState} from '../types/artifact'

/** Session statuses the CMC API recognizes as query param values */
const API_STATUSES = new Set([
  'queued',
  'in_progress',
  'idle',
  'waiting_for_user',
  'completed',
  'failed',
  'timed_out',
  'cancelled',
])

/** UI statuses that map to artifact_state rather than session status */
const ARTIFACT_STATES = new Set(['merged', 'closed'])

/**
 * Generates the API path for repository tasks endpoint
 * @param nwo - Repository name with owner (e.g., "github/github")
 * @returns API path for the repo tasks endpoint
 */
export function repoTasksApiPath(nwo: string): string {
  const [owner, repo] = nwo.split('/')
  return `/agents/repos/${owner}/${repo}/tasks`
}

/**
 * Generates the API path for creating a session for a specific task
 * @param nwo - Repository name with owner (e.g., "github/github")
 * @param taskId - The task ID
 * @returns API path for the task sessions endpoint
 */
export function repoTaskSessionsApiPath(nwo: string, taskId: string): string {
  const [owner, repo] = nwo.split('/')
  return `/agents/repos/${owner}/${repo}/tasks/${taskId}/sessions`
}

/**
 * Builds the URL for the item-pickers assignees endpoint used for collaborator search.
 */
export function collaboratorSearchUrl(params: {owner: string; repo: string; query: string}): string {
  const searchParams = new URLSearchParams(params)
  return `/_ghui/item-pickers/assignees?${searchParams}`
}

export type TasksQueryParams = {
  /** Optional user IDs to filter tasks by creator (repeated creator_id params) */
  creatorIds?: number[]
  /** Optional 1-based page number for paginated results. Only included when pagination is enabled (page is explicitly provided). */
  page?: number
  /** Optional boolean to filter archived tasks (legacy — prefer `tab`) */
  archived?: boolean
  /** Optional boolean to include open and closed task counts in the response */
  includeCounts?: boolean
  /** Optional number of tasks per page */
  pageSize?: number
  /** Optional task type filter (e.g., 'review' for code review tasks only) */
  type?: string
  /** Optional agent IDs to filter tasks by agent */
  agentIds?: number[]
  /** When true, only returns tasks with an associated repository; when false, only returns tasks with no associated repository; when undefined, returns all tasks */
  withRepo?: boolean
  /** Optional custom agent name to filter tasks by exact match */
  customAgentName?: string
  /** Tab filter: 'open' returns open tasks, 'closed' returns closed tasks, omitted returns all */
  tab?: 'open' | 'closed'
  /** Optional detailed status filter (e.g., 'failed', 'merged') — server filters to these statuses */
  statuses?: string[]
  /** Optional sort direction: 'newest' (default) or 'oldest' */
  sort?: string
  /** Optional free-text search query */
  query?: string
  /** Optional agent slugs to filter tasks by agent identifier */
  agentSlugs?: string[]
  /** Optional artifact state filter (e.g., 'open', 'merged', 'closed') */
  artifactStates?: ArtifactState[]
  /** Optional numeric repository ID to scope results to a single repository */
  repositoryId?: number
}

/**
 * Generates query parameters for tasks endpoints.
 * When copilot_task_api_github_rest_style is enabled, uses GitHub REST API-style param names:
 *   page (instead of page_number), per_page (instead of page_size)
 * @param params Query parameters
 * @returns Query string for tasks endpoint
 */
const generateTasksQueryParams = (params: TasksQueryParams): string => {
  const useRestStyle = isFeatureEnabled('copilot_task_api_github_rest_style')
  const searchParams = new URLSearchParams()

  if (params.creatorIds !== undefined && params.creatorIds.length > 0) {
    for (const id of params.creatorIds) {
      searchParams.append('creator_id', String(id))
    }
  }
  if (params.page !== undefined) {
    searchParams.set(useRestStyle ? 'page' : 'page_number', String(params.page))
  }
  if (params.archived !== undefined) {
    searchParams.set('archived', String(params.archived))
  }
  if (params.tab !== undefined) {
    searchParams.set('tab', params.tab)
  }
  if (params.includeCounts !== undefined) {
    searchParams.set('include_counts', String(params.includeCounts))
  }
  if (params.pageSize !== undefined) {
    searchParams.set(useRestStyle ? 'per_page' : 'page_size', String(params.pageSize))
  }
  if (params.type !== undefined) {
    searchParams.set('type', params.type)
  }
  if (params.customAgentName !== undefined) {
    // Custom agents are passed as repeated params per API spec
    const names = params.customAgentName.split(',').filter(Boolean)
    for (const name of names) {
      searchParams.append('custom_agent_name', name)
    }
  }
  if (params.agentIds !== undefined && params.agentIds.length > 0) {
    searchParams.set('include_agent_ids', params.agentIds.join(','))
  }
  if (params.withRepo !== undefined) {
    searchParams.set('with_repo', String(params.withRepo))
  }
  if (params.statuses !== undefined && params.statuses.length > 0) {
    const apiStatuses = params.statuses
      .map(s => (s === 'needs_attention' ? 'waiting_for_user' : s))
      .filter(s => API_STATUSES.has(s))
    const artifactStates = params.statuses.filter(s => ARTIFACT_STATES.has(s))
    if (apiStatuses.length > 0) {
      searchParams.set('status', apiStatuses.join(','))
    }
    for (const state of artifactStates) {
      searchParams.append('artifact_state', state)
    }
  }
  // Append any directly-specified artifact states
  if (params.artifactStates !== undefined) {
    for (const state of params.artifactStates) {
      searchParams.append('artifact_state', state)
    }
  }
  // CMC requires artifact_type when artifact_state is specified
  if (searchParams.has('artifact_state')) {
    searchParams.set('artifact_type', 'pull')
  }
  // Map UI sort direction to CMC API params (sort + direction)
  if (params.sort !== undefined && params.sort !== 'newest') {
    searchParams.set('sort', 'updated_at')
    searchParams.set('direction', 'asc')
  }
  if (params.query !== undefined && params.query !== '') {
    searchParams.set('q', params.query)
  }
  if (params.agentSlugs !== undefined && params.agentSlugs.length > 0) {
    // Separate custom agents (prefixed with "custom:") from built-in agent slugs
    const customAgents = params.agentSlugs.filter(s => s.startsWith('custom:')).map(s => s.slice('custom:'.length))
    const builtinAgents = params.agentSlugs.filter(s => !s.startsWith('custom:'))
    if (builtinAgents.length > 0) {
      searchParams.set('include_agent_ids', builtinAgents.join(','))
    }
    // Custom agents are passed as repeated params per API spec
    for (const name of customAgents) {
      searchParams.append('custom_agent_name', name)
    }
  }

  if (params.repositoryId !== undefined) {
    searchParams.set('repository_id', String(params.repositoryId))
  }

  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

/**
 * Generates the API path for the repository tasks endpoint with query parameters
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param params - Optional query parameters
 * @returns API path for the repo tasks endpoint with query string
 */
export function repoTasksApiPathWithParams(owner: string, repo: string, params?: TasksQueryParams): string {
  const basePath = `/agents/repos/${owner}/${repo}/tasks`
  const query = params ? generateTasksQueryParams(params) : ''
  return `${basePath}${query}`
}

/** API path for the tasks collection endpoint (no repo context). */
export function tasksApiPath(): string {
  return `/agents/tasks`
}

/**
 * Generates the API path for the tasks endpoint with query parameters
 * @param params - Optional query parameters
 * @returns API path for the tasks endpoint with query string
 */
export function tasksApiPathWithParams(params?: TasksQueryParams): string {
  const basePath = tasksApiPath()
  const query = params ? generateTasksQueryParams(params) : ''
  return query ? `${basePath}${query}` : basePath
}

/**
 * Generates the API path for task endpoint
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param taskId - Task ID
 * @returns API path for the task endpoint
 */
export function taskApiPath(owner: string, repo: string, taskId: string): string {
  return `/agents/repos/${owner}/${repo}/tasks/${taskId}`
}

/**
 * Generates the API path for a task by ID only (no repo context required).
 * Used for repo-less tasks where owner/repo may not be known.
 * @param taskId - Task ID
 * @returns API path for the task endpoint
 */
export function taskByIdApiPath(taskId: string): string {
  return `/agents/tasks/${taskId}`
}

/**
 * Generates the API path for fetching multiple tasks by ID in a single request.
 * Mirrors the server-side hydration route (`agents/tasks/batch?task_ids=...`)
 * used by Mission Control's `batch_tasks`. Ids are comma-joined and the value is
 * URL-encoded (commas become `%2C`, which the API decodes back before splitting).
 * @param taskIds - Task IDs to fetch
 * @returns API path for the batch tasks endpoint
 */
export function tasksBatchApiPath(taskIds: string[]): string {
  const searchParams = new URLSearchParams({task_ids: taskIds.join(',')})
  return `/agents/tasks/batch?${searchParams}`
}

/**
 * Generates the API path for archiving a task.
 * Uses repo-scoped path when owner and repo are provided, otherwise uses task-ID path.
 */
export function taskArchiveApiPath(taskId: string, owner?: string, repo?: string): string {
  const base = owner && repo ? taskApiPath(owner, repo, taskId) : taskByIdApiPath(taskId)
  return `${base}/archive`
}

/**
 * Generates the API path for unarchiving a task.
 * Uses repo-scoped path when owner and repo are provided, otherwise uses task-ID path.
 */
export function taskUnarchiveApiPath(taskId: string, owner?: string, repo?: string): string {
  const base = owner && repo ? taskApiPath(owner, repo, taskId) : taskByIdApiPath(taskId)
  return `${base}/unarchive`
}

/**
 * Generates the API path for the environments endpoint with optional filters.
 * Used by the Mission Control Environments API (gated behind
 * `copilot_mission_control_environments_web_kickoff`).
 * @param params - Optional filters: `kind` and/or `status`
 * @returns API path for the environments endpoint with query string
 */
export function environmentsApiPath(params?: {kind?: string; status?: string}): string {
  const basePath = `/agents/environments`
  const searchParams = new URLSearchParams()
  if (params?.kind !== undefined) {
    searchParams.set('kind', params.kind)
  }
  if (params?.status !== undefined) {
    searchParams.set('status', params.status)
  }
  const query = searchParams.toString()
  return query ? `${basePath}?${query}` : basePath
}

/**
 * Generates the API path for deleting an environment. Only `user-local`
 * environments are deletable via this endpoint.
 * @param environmentId - The environment to delete
 * @returns API path for the environment delete endpoint
 */
export function environmentDeleteApiPath(environmentId: string): string {
  return `/agents/environments/${encodeURIComponent(environmentId)}`
}

/**
 * Generates the API path for pull request sessions endpoint
 * @param pullId - Pull request ID
 * @returns API path for the pull sessions endpoint
 */
export function pullSessionsApiPath(pullId: number): string {
  return `/agents/sessions/resource/pull/${pullId}`
}

/**
 * Generates the API path for task inactive logs endpoint
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param taskId - Task ID
 * @returns API path for the task inactive logs endpoint
 */
export function taskInactiveLogsApiPath(owner: string, repo: string, taskId: string): string {
  return `/agents/repos/${owner}/${repo}/tasks/${taskId}/inactive_logs`
}

/**
 * Generates the API path for pull request inactive logs endpoint
 * @param pullId - Pull request ID
 * @returns API path for the pull inactive logs endpoint
 */
export function pullInactiveLogsApiPath(pullId: number): string {
  return `/agents/sessions/resource/pull/${pullId}/inactive_logs`
}

/**
 * Generates the path for the user-level agents endpoint. Unlike the other
 * helpers in this file, this endpoint is served by the github.com monolith
 * rather than the CMC (CAPI) service.
 * @returns Path for the user-level agents endpoint
 */
export function userLevelAgentsMonolithPath(): string {
  return `/copilot/agent-sessions/filterable-agents`
}
